// ============================================================
// MockupScreens.tsx — 6 mockups SVG fiéis das telas do Lume
// ============================================================
// Cada mockup é SVG inline (~3-5 KB), totalmente theme-aware via
// CSS custom properties do critical.css. Reproduz fielmente o
// visual real da app sem depender de captura de imagem.
//
// Phone: viewBox 360×720 (ratio 9:16 / 1:2)
// Desktop: viewBox 1280×800 (ratio 16:10)
// ============================================================

interface MockupProps {
  ariaLabel: string;
}

const COLORS = {
  bgBase: 'var(--color-bg-base)',
  bgSurface: 'var(--color-bg-surface)',
  bgMuted: 'var(--color-bg-muted)',
  bgSubtle: 'var(--color-bg-subtle)',
  fgDefault: 'var(--color-fg-default)',
  fgMuted: 'var(--color-fg-muted)',
  fgSubtle: 'var(--color-fg-subtle)',
  brand50: 'var(--color-brand-50)',
  brand100: 'var(--color-brand-100)',
  brand500: 'var(--color-brand-500)',
  brand700: 'var(--color-brand-700)',
  brand800: 'var(--color-brand-800)',
  brand900: 'var(--color-brand-900)',
  warm100: 'var(--color-warm-100)',
  warm500: 'var(--color-warm-500)',
  warm700: 'var(--color-warm-700)',
  stableFg: 'var(--color-severity-stable-fg)',
  stableBg: 'var(--color-severity-stable-bg)',
  warnFg: 'var(--color-severity-warn-fg)',
  warnBg: 'var(--color-severity-warn-bg)',
  criticalFg: 'var(--color-severity-critical-fg)',
  criticalBg: 'var(--color-severity-critical-bg)',
  border: 'var(--color-border-default)',
  borderStrong: 'var(--color-border-strong)',
};

const fontDisplay = 'var(--font-display)';
const fontBody = 'var(--font-body)';

// Componentes auxiliares ----------------------------------------------------

function StatusBar({ y = 8 }: { y?: number }) {
  return (
    <g aria-hidden="true">
      <text x={20} y={y + 16} fontSize="11" fontFamily={fontDisplay} fill={COLORS.fgMuted}>
        09:42
      </text>
      <g transform={`translate(310 ${y + 6})`} fill={COLORS.fgMuted}>
        <circle cx="3" cy="10" r="1.5" />
        <circle cx="9" cy="10" r="1.5" />
        <circle cx="15" cy="10" r="1.5" />
        <rect x="22" y="6" width="14" height="8" rx="2" stroke={COLORS.fgMuted} fill="none" strokeWidth="1" />
        <rect x="24" y="8" width="10" height="4" rx="1" />
      </g>
    </g>
  );
}

function HeartBadge({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`} aria-hidden="true">
      <circle cx="0" cy="0" r="12" fill={COLORS.bgSurface} stroke={COLORS.criticalFg} strokeWidth="0.7" opacity="0.85" />
      <path
        d="M0 4 C-2 1 -7 -1 -7 -4 C-7 -7 -4 -8 -2 -7 C-1 -6 0 -5 0 -5 C0 -5 1 -6 2 -7 C4 -8 7 -7 7 -4 C7 -1 2 1 0 4 Z"
        fill={COLORS.criticalFg}
        opacity="0.7"
      />
    </g>
  );
}

// 1. N1 Home — autista verbal autoadvogado ----------------------------------

export function MockupN1Home({ ariaLabel }: MockupProps) {
  return (
    <svg
      viewBox="0 0 360 720"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid slice"
      style={{ width: '100%', height: '100%', background: COLORS.bgBase }}
    >
      <StatusBar />
      <HeartBadge x={336} y={36} />

      {/* Header de saudação */}
      <g transform="translate(20 56)">
        <text fontSize="13" fontFamily={fontDisplay} fontWeight="500" fill={COLORS.fgSubtle}>
          Bom dia,
        </text>
        <text x="0" y="26" fontSize="22" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900}>
          Davi
        </text>
        <g transform="translate(0 42)">
          <rect width="118" height="22" rx="11" fill={COLORS.stableBg} />
          <text x="10" y="15" fontSize="10" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.stableFg}>
            ● Humor 7d: Bem
          </text>
          <rect x="126" y="0" width="100" height="22" rx="11" fill={COLORS.brand50} />
          <text x="136" y="15" fontSize="10" fontFamily={fontDisplay} fontWeight="500" fill={COLORS.brand900}>
            Sensor: 72 bpm
          </text>
        </g>
      </g>

      {/* Card Mood Quick */}
      <g transform="translate(16 144)">
        <rect width="328" height="124" rx="16" fill={COLORS.bgSurface} stroke={COLORS.border} />
        <text x="16" y="24" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgSubtle} letterSpacing="0.05em">
          COMO ME SINTO AGORA
        </text>
        {[
          { e: '😣', l: 'Muito mal', c: COLORS.criticalBg },
          { e: '😟', l: 'Mal', c: COLORS.warnBg },
          { e: '😐', l: 'Mais ou menos', c: COLORS.bgMuted },
          { e: '🙂', l: 'Bem', c: COLORS.stableBg },
          { e: '😄', l: 'Muito bem', c: COLORS.warm100 },
        ].map((item, i) => (
          <g key={i} transform={`translate(${16 + i * 60} 40)`}>
            <rect width="52" height="68" rx="12" fill={item.c} stroke={i === 3 ? COLORS.warm700 : 'transparent'} strokeWidth="2.5" />
            <text x="26" y="38" fontSize="22" textAnchor="middle">
              {item.e}
            </text>
            <text x="26" y="58" fontSize="7.5" textAnchor="middle" fill={COLORS.fgMuted} fontFamily={fontBody}>
              {item.l}
            </text>
          </g>
        ))}
      </g>

      {/* Visual Schedule */}
      <g transform="translate(16 286)">
        <text fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgSubtle} letterSpacing="0.05em">
          AGENDA DE HOJE
        </text>
        {[
          { time: '09:00', label: 'Café da manhã', icon: '🍞', done: true },
          { time: '10:00', label: 'Terapia ABA', icon: '🧩', done: true, current: true },
          { time: '12:00', label: 'Almoço', icon: '🍽️', done: false },
          { time: '15:00', label: 'Hora livre', icon: '🎨', done: false },
        ].map((item, i) => (
          <g key={i} transform={`translate(0 ${16 + i * 56})`}>
            <rect
              width="328"
              height="48"
              rx="12"
              fill={item.current ? COLORS.warm100 : COLORS.bgSurface}
              stroke={item.current ? COLORS.warm700 : COLORS.border}
              strokeWidth={item.current ? '2' : '1'}
            />
            <text x="16" y="20" fontSize="10" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgSubtle}>
              {item.time}
            </text>
            <text x="16" y="36" fontSize="13" fontFamily={fontBody} fontWeight="500" fill={COLORS.fgDefault}>
              {item.label}
            </text>
            <text x="296" y="32" fontSize="22">
              {item.icon}
            </text>
            {item.done ? (
              <circle cx="280" cy="24" r="8" fill={COLORS.stableFg} opacity="0.85" />
            ) : null}
          </g>
        ))}
      </g>

      {/* Atalho assistente */}
      <g transform="translate(16 542)">
        <rect width="328" height="68" rx="14" fill={COLORS.brand800} />
        <circle cx="38" cy="34" r="20" fill={COLORS.warm500} />
        <text x="38" y="40" textAnchor="middle" fontSize="20">
          ✨
        </text>
        <text x="74" y="30" fontSize="13" fontFamily={fontDisplay} fontWeight="600" fill="#FFFFFF">
          Falar com o assistente
        </text>
        <text x="74" y="46" fontSize="10.5" fontFamily={fontBody} fill="#FFFFFF" opacity="0.75">
          Texto, sem julgamento
        </text>
      </g>

      {/* Bottom nav (sidebar mobile) */}
      <g transform="translate(0 632)">
        <rect width="360" height="72" fill={COLORS.bgSurface} stroke={COLORS.border} />
        {['🏠', '📅', '🎮', '🌬️'].map((emoji, i) => (
          <g key={i} transform={`translate(${30 + i * 80} 16)`}>
            <rect width="56" height="40" rx="12" fill={i === 0 ? COLORS.brand100 : 'transparent'} />
            <text x="28" y="26" textAnchor="middle" fontSize="18">
              {emoji}
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

// 2. N2 Home — autista com pictogramas + agenda visual ----------------------

export function MockupN2Home({ ariaLabel }: MockupProps) {
  return (
    <svg
      viewBox="0 0 360 720"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid slice"
      style={{ width: '100%', height: '100%', background: COLORS.bgBase }}
    >
      <StatusBar />
      <HeartBadge x={336} y={36} />

      {/* Header */}
      <g transform="translate(20 60)">
        <text fontSize="22" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900}>
          Olá, Helena
        </text>
        <text x="0" y="22" fontSize="13" fontFamily={fontBody} fill={COLORS.fgMuted}>
          Bom dia! ☀️
        </text>
      </g>

      {/* Mood pictográfico 1-5 */}
      <g transform="translate(16 130)">
        <rect width="328" height="180" rx="18" fill={COLORS.bgSurface} stroke={COLORS.border} />
        <text x="16" y="28" fontSize="14" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgDefault}>
          Como você está?
        </text>
        <g transform="translate(16 50)">
          {[
            { e: '😣', c: COLORS.criticalBg, fc: COLORS.criticalFg },
            { e: '😟', c: COLORS.warnBg, fc: COLORS.warnFg },
            { e: '😐', c: COLORS.bgMuted, fc: COLORS.fgMuted },
            { e: '🙂', c: COLORS.stableBg, fc: COLORS.stableFg },
            { e: '😄', c: COLORS.warm100, fc: COLORS.warm700 },
          ].map((item, i) => (
            <g key={i} transform={`translate(${i * 60} 0)`}>
              <rect width="52" height="100" rx="14" fill={item.c} stroke={i === 4 ? COLORS.warm700 : 'transparent'} strokeWidth="3" />
              <text x="26" y="50" fontSize="32" textAnchor="middle">
                {item.e}
              </text>
              <text x="26" y="84" fontSize="22" textAnchor="middle" fill={item.fc}>
                {'★'.repeat(i + 1).slice(0, 3)}
              </text>
            </g>
          ))}
        </g>
      </g>

      {/* Pictogramas grandes — atalhos */}
      <g transform="translate(16 332)">
        <text fontSize="13" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgSubtle} letterSpacing="0.04em">
          O QUE VAMOS FAZER?
        </text>
        <g transform="translate(0 16)">
          {[
            { e: '🗣️', l: 'Falar', c: COLORS.brand100 },
            { e: '🌬️', l: 'Calma', c: COLORS.warm100 },
            { e: '🎨', l: 'Distrair', c: COLORS.stableBg },
          ].map((item, i) => (
            <g key={i} transform={`translate(${i * 110} 0)`}>
              <rect width="100" height="120" rx="18" fill={item.c} stroke={COLORS.border} />
              <text x="50" y="60" textAnchor="middle" fontSize="48">
                {item.e}
              </text>
              <text x="50" y="100" textAnchor="middle" fontSize="14" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.brand900}>
                {item.l}
              </text>
            </g>
          ))}
        </g>
      </g>

      {/* Agenda visual */}
      <g transform="translate(16 488)">
        <text fontSize="13" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgSubtle} letterSpacing="0.04em">
          AGENDA
        </text>
        {[
          { e: '🍞', l: 'Café', t: '09:00', done: true },
          { e: '🧩', l: 'Terapia', t: '10:00', current: true },
          { e: '🍽️', l: 'Almoço', t: '12:00' },
        ].map((item, i) => (
          <g key={i} transform={`translate(${i * 110} 18)`}>
            <rect
              width="100"
              height="100"
              rx="14"
              fill={item.current ? COLORS.warm100 : COLORS.bgSurface}
              stroke={item.current ? COLORS.warm700 : COLORS.border}
              strokeWidth={item.current ? '2.5' : '1'}
            />
            <text x="50" y="48" textAnchor="middle" fontSize="36">
              {item.e}
            </text>
            <text x="50" y="74" textAnchor="middle" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.brand900}>
              {item.l}
            </text>
            <text x="50" y="89" textAnchor="middle" fontSize="9.5" fontFamily={fontBody} fill={COLORS.fgMuted}>
              {item.t}
            </text>
            {item.done ? (
              <circle cx="84" cy="16" r="8" fill={COLORS.stableFg} />
            ) : null}
          </g>
        ))}
      </g>
    </svg>
  );
}

// 3. N3 Home — CAA-first, low-stim ------------------------------------------

export function MockupN3Home({ ariaLabel }: MockupProps) {
  return (
    <svg
      viewBox="0 0 360 720"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid slice"
      style={{ width: '100%', height: '100%', background: COLORS.bgBase }}
    >
      <StatusBar />
      <HeartBadge x={336} y={36} />

      {/* Greeting low-stim */}
      <g transform="translate(180 80)">
        <text textAnchor="middle" fontSize="28" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900}>
          Olá
        </text>
        <text textAnchor="middle" y="32" fontSize="20" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.brand700}>
          Lume
        </text>
      </g>

      {/* Mood 1-3 (triste / tranquilo / feliz) — pictogramas grandes */}
      <g transform="translate(16 160)">
        {[
          { e: '😢', l: 'TRISTE', c: COLORS.criticalBg, fc: COLORS.criticalFg },
          { e: '😌', l: 'TRANQUILO', c: COLORS.stableBg, fc: COLORS.stableFg, sel: true },
          { e: '😊', l: 'FELIZ', c: COLORS.warm100, fc: COLORS.warm700 },
        ].map((item, i) => (
          <g key={i} transform={`translate(${i * 110} 0)`}>
            <rect
              width="100"
              height="120"
              rx="18"
              fill={item.c}
              stroke={item.sel ? item.fc : COLORS.border}
              strokeWidth={item.sel ? '4' : '1'}
            />
            <text x="50" y="68" textAnchor="middle" fontSize="56">
              {item.e}
            </text>
            <text x="50" y="100" textAnchor="middle" fontSize="11" fontFamily={fontDisplay} fontWeight="700" fill={item.fc} letterSpacing="0.08em">
              {item.l}
            </text>
          </g>
        ))}
      </g>

      {/* CAA grande — 4 pictogramas 100×100 */}
      <g transform="translate(40 312)">
        <text fontSize="13" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgSubtle} letterSpacing="0.06em" textAnchor="middle" x="140" y="0">
          O QUE EU QUERO?
        </text>
        <g transform="translate(0 16)">
          {[
            { e: '💬', l: 'FALAR', c: COLORS.brand100, fc: COLORS.brand900 },
            { e: '🌬️', l: 'CALMA', c: COLORS.warm100, fc: COLORS.warm700 },
            { e: '🍽️', l: 'COMER', c: COLORS.bgMuted, fc: COLORS.fgDefault },
            { e: '🛏️', l: 'DORMIR', c: COLORS.stableBg, fc: COLORS.stableFg },
          ].map((item, i) => {
            const col = i % 2;
            const row = Math.floor(i / 2);
            return (
              <g key={i} transform={`translate(${col * 150} ${row * 150})`}>
                <rect width="130" height="130" rx="22" fill={item.c} stroke={COLORS.border} strokeWidth="1.5" />
                <text x="65" y="78" textAnchor="middle" fontSize="64">
                  {item.e}
                </text>
                <text x="65" y="115" textAnchor="middle" fontSize="13" fontFamily={fontDisplay} fontWeight="700" fill={item.fc} letterSpacing="0.06em">
                  {item.l}
                </text>
              </g>
            );
          })}
        </g>
      </g>
    </svg>
  );
}

// 4. Painel médico — desktop, lista + chart ---------------------------------

export function MockupPainelMedico({ ariaLabel }: MockupProps) {
  return (
    <svg
      viewBox="0 0 1280 800"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid slice"
      style={{ width: '100%', height: '100%', background: COLORS.bgBase }}
    >
      {/* Top bar */}
      <g>
        <rect width="1280" height="60" fill={COLORS.bgSurface} />
        <line x1="0" y1="60" x2="1280" y2="60" stroke={COLORS.border} />
        <g transform="translate(28 18)">
          <circle cx="14" cy="14" r="14" fill={COLORS.brand800} />
          <text x="14" y="20" textAnchor="middle" fontSize="14" fill="#FFFFFF" fontFamily={fontDisplay} fontWeight="700">
            ✦
          </text>
          <text x="40" y="22" fontSize="20" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900} letterSpacing="-0.02em">
            lume
          </text>
        </g>
        <text x="1080" y="36" fontSize="13" fontFamily={fontBody} fill={COLORS.fgMuted}>
          Dra. Marina · Médica
        </text>
        <circle cx="1230" cy="30" r="14" fill={COLORS.brand100} />
        <text x="1230" y="35" textAnchor="middle" fontSize="12" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900}>
          M
        </text>
      </g>

      {/* Sidebar lista pacientes */}
      <g>
        <rect x="0" y="60" width="320" height="740" fill={COLORS.bgSurface} />
        <line x1="320" y1="60" x2="320" y2="800" stroke={COLORS.border} />

        <text x="20" y="98" fontSize="14" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.fgDefault}>
          Pacientes (7)
        </text>
        <text x="20" y="118" fontSize="11" fontFamily={fontBody} fill={COLORS.fgSubtle}>
          Ordenado por última atividade
        </text>

        {/* search box */}
        <rect x="20" y="132" width="280" height="32" rx="16" fill={COLORS.bgMuted} stroke={COLORS.border} />
        <text x="36" y="152" fontSize="11" fontFamily={fontBody} fill={COLORS.fgSubtle}>
          🔍 Buscar paciente
        </text>

        {/* lista de pacientes */}
        {[
          { name: 'Davi', meta: 'N1 · há 2h', sev: 'critical', selected: true, init: 'D' },
          { name: 'Helena', meta: 'N2 · há 4h', sev: 'warn', init: 'H' },
          { name: 'Miguel', meta: 'N3 · há 1d', sev: 'stable', init: 'M' },
          { name: 'Tiago', meta: 'N1 · há 1d', sev: 'stable', init: 'T' },
          { name: 'Sofia', meta: 'N2 · há 2d', sev: 'stable', init: 'S' },
        ].map((p, i) => {
          const sevColor =
            p.sev === 'critical' ? COLORS.criticalFg : p.sev === 'warn' ? COLORS.warnFg : COLORS.stableFg;
          return (
            <g key={i} transform={`translate(0 ${184 + i * 64})`}>
              <rect
                x="12"
                width="296"
                height="56"
                rx="10"
                fill={p.selected ? COLORS.brand100 : 'transparent'}
              />
              <circle cx="36" cy="28" r="18" fill={COLORS.brand800} />
              <text x="36" y="34" textAnchor="middle" fontSize="13" fontFamily={fontDisplay} fontWeight="700" fill="#FFFFFF">
                {p.init}
              </text>
              <text x="64" y="22" fontSize="13" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgDefault}>
                {p.name}
              </text>
              <text x="64" y="38" fontSize="11" fontFamily={fontBody} fill={COLORS.fgSubtle}>
                {p.meta}
              </text>
              <circle cx="290" cy="28" r="5" fill={sevColor} />
            </g>
          );
        })}
      </g>

      {/* Main content — paciente Davi */}
      <g transform="translate(360 92)">
        <text fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.fgSubtle} letterSpacing="0.06em">
          PACIENTE
        </text>
        <text y="32" fontSize="28" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900} letterSpacing="-0.02em">
          Davi (sintético)
        </text>
        <g transform="translate(0 50)">
          <rect width="78" height="22" rx="11" fill={COLORS.brand100} />
          <text x="39" y="15" textAnchor="middle" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.brand900}>
            Nível 1
          </text>
          <rect x="86" width="120" height="22" rx="11" fill={COLORS.stableBg} />
          <text x="146" y="15" textAnchor="middle" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.stableFg}>
            ● Atividade hoje
          </text>
          <rect x="214" width="160" height="22" rx="11" fill={COLORS.warm100} />
          <text x="294" y="15" textAnchor="middle" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.warm700}>
            📡 Android + Polar H10
          </text>
        </g>
      </g>

      {/* Card Sumário IA */}
      <g transform="translate(360 200)">
        <rect width="560" height="180" rx="14" fill={COLORS.bgSurface} stroke={COLORS.border} />
        <g transform="translate(20 22)">
          <rect width="74" height="22" rx="11" fill={COLORS.warnBg} />
          <text x="37" y="15" textAnchor="middle" fontSize="10" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.warnFg}>
            ⚠ HIPÓTESE
          </text>
          <text x="86" y="15" fontSize="11" fontFamily={fontBody} fill={COLORS.fgSubtle}>
            Sumário IA clínica · 09:38
          </text>
        </g>
        <text x="20" y="76" fontSize="13" fontFamily={fontBody} fill={COLORS.fgDefault}>
          Davi apresentou 3 episódios de sobrecarga sensorial nos últimos 7 dias,
        </text>
        <text x="20" y="94" fontSize="13" fontFamily={fontBody} fill={COLORS.fgDefault}>
          todos no período da tarde. HRV-RMSSD caiu de média 38ms para 22ms,
        </text>
        <text x="20" y="112" fontSize="13" fontFamily={fontBody} fill={COLORS.fgDefault}>
          sugerindo aumento de carga autonômica. Adesão à risperidona: 89%.
        </text>
        <text x="20" y="148" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill={COLORS.brand700}>
          Ver evidências citadas (3) →
        </text>
      </g>

      {/* Timeline chart */}
      <g transform="translate(940 200)">
        <rect width="300" height="180" rx="14" fill={COLORS.bgSurface} stroke={COLORS.border} />
        <text x="20" y="28" fontSize="12" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.fgDefault}>
          FC + HRV (24h)
        </text>
        {/* sparkline */}
        <polyline
          points="20,140 50,128 80,135 110,118 140,108 170,116 200,98 230,88 260,102 280,118"
          fill="none"
          stroke={COLORS.brand700}
          strokeWidth="2"
        />
        <polyline
          points="20,150 50,152 80,148 110,145 140,140 170,138 200,135 230,132 260,128 280,125"
          fill="none"
          stroke={COLORS.warm700}
          strokeWidth="2"
          strokeDasharray="3 3"
        />
        <text x="20" y="170" fontSize="9" fontFamily={fontBody} fill={COLORS.fgSubtle}>
          ● FC bpm   ◌ HRV-RMSSD ms
        </text>
      </g>

      {/* Linha do tempo */}
      <g transform="translate(360 420)">
        <text fontSize="13" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.fgDefault}>
          Linha do tempo · últimos 7 dias
        </text>
        <line x1="0" y1="50" x2="880" y2="50" stroke={COLORS.border} strokeWidth="2" />
        {[
          { x: 60, type: 'crisis', label: 'Crise', day: 'Seg' },
          { x: 180, type: 'mood', label: 'Humor', day: 'Ter' },
          { x: 280, type: 'mood', label: 'Humor', day: 'Ter' },
          { x: 380, type: 'sensory', label: 'Sensorial', day: 'Qua' },
          { x: 480, type: 'crisis', label: 'Crise', day: 'Qui' },
          { x: 580, type: 'med', label: 'Dose', day: 'Qui' },
          { x: 700, type: 'mood', label: 'Humor', day: 'Sex' },
          { x: 820, type: 'med', label: 'Dose', day: 'Hoje' },
        ].map((ev, i) => {
          const c =
            ev.type === 'crisis'
              ? COLORS.criticalFg
              : ev.type === 'sensory'
              ? COLORS.warnFg
              : ev.type === 'mood'
              ? COLORS.brand700
              : COLORS.stableFg;
          return (
            <g key={i} transform={`translate(${ev.x} 0)`}>
              <line x1="0" y1="50" x2="0" y2="68" stroke={c} strokeWidth="2" />
              <circle cx="0" cy="50" r="6" fill={c} />
              <text x="0" y="84" textAnchor="middle" fontSize="9" fontFamily={fontDisplay} fontWeight="600" fill={c}>
                {ev.label}
              </text>
              <text x="0" y="98" textAnchor="middle" fontSize="9" fontFamily={fontBody} fill={COLORS.fgSubtle}>
                {ev.day}
              </text>
            </g>
          );
        })}
      </g>

      {/* Tabs */}
      <g transform="translate(360 600)">
        {['Resumo', 'Eventos', 'Wearable', 'Engajamento', 'Medicação', 'Plano'].map((tab, i) => (
          <g key={i} transform={`translate(${i * 110} 0)`}>
            <rect
              width="100"
              height="34"
              rx="8"
              fill={i === 3 ? COLORS.brand100 : 'transparent'}
              stroke={i === 3 ? COLORS.brand500 : COLORS.border}
            />
            <text
              x="50"
              y="22"
              textAnchor="middle"
              fontSize="11"
              fontFamily={fontDisplay}
              fontWeight={i === 3 ? '700' : '500'}
              fill={i === 3 ? COLORS.brand900 : COLORS.fgMuted}
            >
              {tab}
            </text>
          </g>
        ))}
      </g>

      {/* Mini engagement heatmap preview */}
      <g transform="translate(360 654)">
        <rect width="880" height="120" rx="12" fill={COLORS.bgSurface} stroke={COLORS.border} />
        <text x="20" y="26" fontSize="12" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.fgDefault}>
          Heatmap de engajamento sensorial · 30 dias
        </text>
        {Array.from({ length: 30 }).map((_, day) =>
          Array.from({ length: 7 }).map((__, hour) => {
            const intensity = Math.random();
            const opacity = intensity < 0.3 ? 0.08 : intensity < 0.6 ? 0.35 : 0.7;
            return (
              <rect
                key={`${day}-${hour}`}
                x={20 + day * 28}
                y={42 + hour * 10}
                width="24"
                height="8"
                rx="2"
                fill={COLORS.warm700}
                opacity={opacity}
              />
            );
          }),
        )}
      </g>
    </svg>
  );
}

// 5. Modo Distração — BubblePop ---------------------------------------------

export function MockupDistracao({ ariaLabel }: MockupProps) {
  return (
    <svg
      viewBox="0 0 360 720"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid slice"
      style={{
        width: '100%',
        height: '100%',
        background: 'linear-gradient(180deg, #1a3a5c 0%, #0f2640 100%)',
      }}
    >
      <StatusBar />

      {/* Header com botão Sair */}
      <g transform="translate(20 50)">
        <rect width="80" height="32" rx="16" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" />
        <text x="40" y="22" textAnchor="middle" fontSize="12" fontFamily={fontDisplay} fontWeight="600" fill="#FFFFFF">
          ← Sair
        </text>
        <text x="180" y="22" textAnchor="middle" fontSize="14" fontFamily={fontDisplay} fontWeight="700" fill="#FFFFFF" letterSpacing="0.04em">
          BUBBLE POP
        </text>
        <g transform="translate(286 6)">
          <rect width="54" height="22" rx="11" fill="rgba(255,200,87,0.2)" stroke="rgba(255,200,87,0.5)" />
          <text x="27" y="15" textAnchor="middle" fontSize="10" fontFamily={fontDisplay} fontWeight="600" fill="#FFC857">
            🔊 Som
          </text>
        </g>
      </g>

      {/* Bubbles em diferentes posições + tamanhos */}
      <g>
        {[
          { x: 80, y: 200, r: 36, c: '#FFC857' },
          { x: 200, y: 160, r: 48, c: '#FF8FA3' },
          { x: 280, y: 240, r: 28, c: '#A8D8B9' },
          { x: 130, y: 320, r: 52, c: '#9CC0DC' },
          { x: 260, y: 380, r: 40, c: '#FFC857', popped: true },
          { x: 80, y: 420, r: 32, c: '#C3B5E5' },
          { x: 200, y: 480, r: 44, c: '#FF8FA3' },
          { x: 290, y: 530, r: 30, c: '#A8D8B9' },
          { x: 120, y: 580, r: 38, c: '#9CC0DC' },
        ].map((b, i) => (
          <g key={i}>
            <circle cx={b.x} cy={b.y} r={b.r} fill={b.c} opacity={b.popped ? 0.2 : 0.85} />
            {!b.popped ? (
              <circle
                cx={b.x - b.r * 0.3}
                cy={b.y - b.r * 0.3}
                r={b.r * 0.25}
                fill="#FFFFFF"
                opacity="0.4"
              />
            ) : (
              <g>
                {/* "estouradas" pontos */}
                {Array.from({ length: 6 }).map((_, j) => {
                  const angle = (j / 6) * Math.PI * 2;
                  return (
                    <circle
                      key={j}
                      cx={b.x + Math.cos(angle) * b.r * 1.3}
                      cy={b.y + Math.sin(angle) * b.r * 1.3}
                      r="3"
                      fill={b.c}
                      opacity="0.6"
                    />
                  );
                })}
              </g>
            )}
          </g>
        ))}
      </g>

      {/* Bottom bar — counter passivo discreto */}
      <g transform="translate(0 660)">
        <rect width="360" height="60" fill="rgba(0,0,0,0.4)" />
        <text x="20" y="36" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill="rgba(255,255,255,0.65)" letterSpacing="0.06em">
          SESSÃO
        </text>
        <text x="20" y="50" fontSize="13" fontFamily={fontBody} fill="#FFFFFF">
          02:14 · 34 toques
        </text>
        <text x="200" y="36" fontSize="11" fontFamily={fontDisplay} fontWeight="600" fill="rgba(255,255,255,0.65)" letterSpacing="0.06em">
          RITMO
        </text>
        <text x="200" y="50" fontSize="13" fontFamily={fontBody} fill="#FFFFFF">
          15.2 toques/min
        </text>
      </g>
    </svg>
  );
}

// 6. Modo Crise — botão SAMU 192 grande -------------------------------------

export function MockupCrise({ ariaLabel }: MockupProps) {
  return (
    <svg
      viewBox="0 0 360 720"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid slice"
      style={{ width: '100%', height: '100%', background: COLORS.bgBase }}
    >
      <StatusBar />

      {/* Aviso topo */}
      <g transform="translate(0 40)">
        <rect width="360" height="46" fill={COLORS.warnBg} />
        <text x="180" y="20" textAnchor="middle" fontSize="11" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.warnFg} letterSpacing="0.04em">
          MODO CRISE — ROTEIRO ADAPTADO
        </text>
        <text x="180" y="36" textAnchor="middle" fontSize="10" fontFamily={fontBody} fill={COLORS.warnFg}>
          📍 Localização será compartilhada com SAMU se você apertar
        </text>
      </g>

      {/* Header */}
      <g transform="translate(20 110)">
        <text fontSize="22" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900}>
          Está acontecendo
        </text>
        <text x="0" y="28" fontSize="22" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900}>
          uma crise?
        </text>
        <text x="0" y="64" fontSize="14" fontFamily={fontBody} fill={COLORS.fgMuted}>
          Você não está sozinho. Aqui estão os
        </text>
        <text x="0" y="82" fontSize="14" fontFamily={fontBody} fill={COLORS.fgMuted}>
          passos imediatos:
        </text>
      </g>

      {/* Lista de passos */}
      <g transform="translate(20 220)">
        {[
          { n: '1', t: 'Respire', s: 'Inspire 4s · segure 4s · solte 6s' },
          { n: '2', t: 'Garanta segurança', s: 'Afaste objetos perigosos próximos' },
          { n: '3', t: 'Se houver risco, ligue', s: 'O botão abaixo chama o SAMU 192' },
        ].map((step, i) => (
          <g key={i} transform={`translate(0 ${i * 56})`}>
            <circle cx="20" cy="20" r="16" fill={COLORS.brand100} />
            <text x="20" y="26" textAnchor="middle" fontSize="14" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.brand900}>
              {step.n}
            </text>
            <text x="48" y="18" fontSize="14" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.fgDefault}>
              {step.t}
            </text>
            <text x="48" y="36" fontSize="12" fontFamily={fontBody} fill={COLORS.fgMuted}>
              {step.s}
            </text>
          </g>
        ))}
      </g>

      {/* Botão SAMU 192 grande */}
      <g transform="translate(20 426)">
        <rect width="320" height="120" rx="22" fill={COLORS.criticalFg} />
        <circle cx="50" cy="60" r="28" fill="#FFFFFF" opacity="0.18" />
        <text x="50" y="68" textAnchor="middle" fontSize="32">
          📞
        </text>
        <text x="98" y="48" fontSize="22" fontFamily={fontDisplay} fontWeight="700" fill="#FFFFFF" letterSpacing="-0.01em">
          Chamar SAMU 192
        </text>
        <text x="98" y="72" fontSize="13" fontFamily={fontBody} fill="#FFFFFF" opacity="0.85">
          Atendimento médico de urgência
        </text>
        <text x="98" y="92" fontSize="11" fontFamily={fontBody} fill="#FFFFFF" opacity="0.7">
          Disponível 24h · gratuito
        </text>
      </g>

      {/* Botão CVV (188) secundário */}
      <g transform="translate(20 566)">
        <rect width="320" height="68" rx="14" fill={COLORS.bgSurface} stroke={COLORS.criticalFg} strokeWidth="2" />
        <circle cx="38" cy="34" r="18" fill={COLORS.criticalBg} />
        <text x="38" y="40" textAnchor="middle" fontSize="18">
          💛
        </text>
        <text x="68" y="30" fontSize="14" fontFamily={fontDisplay} fontWeight="700" fill={COLORS.criticalFg}>
          188 — CVV (24h)
        </text>
        <text x="68" y="48" fontSize="11" fontFamily={fontBody} fill={COLORS.fgMuted}>
          Centro de Valorização da Vida
        </text>
      </g>

      {/* Footer aviso */}
      <g transform="translate(20 654)">
        <text fontSize="9.5" fontFamily={fontBody} fill={COLORS.fgSubtle}>
          Este aplicativo NÃO substitui atendimento médico.
        </text>
        <text x="0" y="14" fontSize="9.5" fontFamily={fontBody} fill={COLORS.fgSubtle}>
          Em emergência, vá ao serviço de urgência mais próximo.
        </text>
      </g>
    </svg>
  );
}
